import {
  sourcingEventsResponseSchema,
  bidsResponseSchema,
  type SourcingEventsResponse,
  type BidsResponse,
  type BidLine,
  type SourcingEvent,
  type Bid,
} from '../types/sourcing';
import {
  FetchError, SubmitError, WithdrawError 
} from '../errors';
import {
  authFetch, buildApiUrl 
} from '../lib/api-client';

// Mock data for development
const mockSourcingEvents: SourcingEvent[] = [
  {
    eventId: 'evt-001',
    eventNumber: 'SE-2026-001',
    title: 'Fresh Produce - Q1 Supply',
    description: 'Quarterly fresh produce supply for cruise fleet',
    eventType: 'reverse_auction',
    status: 'open',
    deliveryZone: 'Port of Miami',
    deliverySiteId: 'site-miami-01',
    deliverySiteName: 'MS Celebration',
    requiredDeliveryDate: '2026-02-15',
    bidDeadline: '2026-02-10T17:00:00Z',
    auctionStartTime: '2026-02-08T09:00:00Z',
    auctionEndTime: '2026-02-10T17:00:00Z',
    currentLowestBid: 45000,
    startingPrice: 50000,
    lines: [
      {
        lineId: 'line-001',
        itemId: 'item-tomatoes',
        itemName: 'Roma Tomatoes',
        sku: 'PROD-TOM-001',
        category: 'Fresh Produce',
        quantity: 500,
        unit: 'kg',
      },
      {
        lineId: 'line-002',
        itemId: 'item-lettuce',
        itemName: 'Iceberg Lettuce',
        sku: 'PROD-LET-001',
        category: 'Fresh Produce',
        quantity: 300,
        unit: 'heads',
      },
    ],
    estimatedValue: 50000,
    currency: 'USD',
    totalBids: 5,
    createdBy: 'user-001',
    createdByName: 'John Smith',
    createdAt: '2026-01-20T10:00:00Z',
    updatedAt: '2026-01-28T14:30:00Z',
  },
  {
    eventId: 'evt-002',
    eventNumber: 'SE-2026-002',
    title: 'Frozen Seafood Supply',
    description: 'Monthly frozen seafood for Mediterranean fleet',
    eventType: 'sealed_proposal',
    status: 'open',
    deliveryZone: 'Port Everglades',
    deliverySiteId: 'site-everglades-01',
    deliverySiteName: 'MS Paradise',
    requiredDeliveryDate: '2026-02-20',
    bidDeadline: '2026-02-12T23:59:59Z',
    lines: [
      {
        lineId: 'line-003',
        itemId: 'item-salmon',
        itemName: 'Atlantic Salmon Fillets',
        sku: 'SEA-SAL-001',
        category: 'Frozen Seafood',
        quantity: 200,
        unit: 'kg',
      },
      {
        lineId: 'line-004',
        itemId: 'item-shrimp',
        itemName: 'Tiger Shrimp 16/20',
        sku: 'SEA-SHR-001',
        category: 'Frozen Seafood',
        quantity: 150,
        unit: 'kg',
      },
    ],
    estimatedValue: 35000,
    currency: 'USD',
    totalBids: 3,
    createdBy: 'user-002',
    createdByName: 'Maria Garcia',
    createdAt: '2026-01-22T09:00:00Z',
    updatedAt: '2026-01-25T11:00:00Z',
  },
  {
    eventId: 'evt-003',
    eventNumber: 'SE-2026-003',
    title: 'Dairy Products - Weekly Supply',
    description: 'Weekly dairy supply for all ships',
    eventType: 'reverse_auction',
    status: 'scheduled',
    deliveryZone: 'Port of Miami',
    deliverySiteId: 'site-miami-02',
    deliverySiteName: 'MS Horizon',
    requiredDeliveryDate: '2026-02-25',
    bidDeadline: '2026-02-18T17:00:00Z',
    auctionStartTime: '2026-02-15T09:00:00Z',
    auctionEndTime: '2026-02-18T17:00:00Z',
    startingPrice: 25000,
    lines: [
      {
        lineId: 'line-005',
        itemId: 'item-milk',
        itemName: 'Whole Milk',
        sku: 'DAI-MLK-001',
        category: 'Dairy',
        quantity: 1000,
        unit: 'liters',
      },
    ],
    estimatedValue: 25000,
    currency: 'USD',
    totalBids: 0,
    createdBy: 'user-001',
    createdByName: 'John Smith',
    createdAt: '2026-01-28T08:00:00Z',
    updatedAt: '2026-01-28T08:00:00Z',
  },
];

const mockBids: Bid[] = [
  {
    bidId: 'bid-001',
    eventId: 'evt-001',
    supplierId: 'sup-sysco',
    supplierName: 'Sysco Foods',
    status: 'submitted',
    lines: [
      {
        lineId: 'line-001',
        itemId: 'item-tomatoes',
        unitPrice: 3.50,
        totalPrice: 1750,
        leadTimeDays: 2,
      },
      {
        lineId: 'line-002',
        itemId: 'item-lettuce',
        unitPrice: 2.00,
        totalPrice: 600,
        leadTimeDays: 2,
      },
    ],
    subtotal: 2350,
    tax: 0,
    total: 2350,
    currency: 'USD',
    validUntil: '2026-02-15T23:59:59Z',
    rank: 2,
    isLowestBid: false,
    submittedAt: '2026-01-29T10:30:00Z',
    createdAt: '2026-01-29T10:00:00Z',
    updatedAt: '2026-01-29T10:30:00Z',
  },
  {
    bidId: 'bid-002',
    eventId: 'evt-old-001',
    supplierId: 'sup-sysco',
    supplierName: 'Sysco Foods',
    status: 'accepted',
    lines: [
      {
        lineId: 'line-old-001',
        itemId: 'item-beef',
        unitPrice: 15.00,
        totalPrice: 7500,
        leadTimeDays: 3,
      },
    ],
    subtotal: 7500,
    tax: 0,
    total: 7500,
    currency: 'USD',
    validUntil: '2026-01-15T23:59:59Z',
    submittedAt: '2026-01-05T14:00:00Z',
    createdAt: '2026-01-05T13:00:00Z',
    updatedAt: '2026-01-10T09:00:00Z',
  },
  {
    bidId: 'bid-003',
    eventId: 'evt-old-002',
    supplierId: 'sup-sysco',
    supplierName: 'Sysco Foods',
    status: 'rejected',
    lines: [
      {
        lineId: 'line-old-002',
        itemId: 'item-chicken',
        unitPrice: 8.00,
        totalPrice: 4000,
        leadTimeDays: 2,
      },
    ],
    subtotal: 4000,
    tax: 0,
    total: 4000,
    currency: 'USD',
    validUntil: '2026-01-20T23:59:59Z',
    submittedAt: '2026-01-08T11:00:00Z',
    createdAt: '2026-01-08T10:00:00Z',
    updatedAt: '2026-01-12T16:00:00Z',
  },
  {
    bidId: 'bid-004',
    eventId: 'evt-old-003',
    supplierId: 'sup-sysco',
    supplierName: 'Sysco Foods',
    status: 'outbid',
    lines: [
      {
        lineId: 'line-old-003',
        itemId: 'item-pork',
        unitPrice: 12.00,
        totalPrice: 6000,
        leadTimeDays: 3,
      },
    ],
    subtotal: 6000,
    tax: 0,
    total: 6000,
    currency: 'USD',
    validUntil: '2026-01-25T23:59:59Z',
    submittedAt: '2026-01-15T09:00:00Z',
    createdAt: '2026-01-15T08:00:00Z',
    updatedAt: '2026-01-18T14:00:00Z',
  },
];

// Simulated API delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function getAvailableSourcingEvents(
  deliveryZones: string[]
): Promise<SourcingEventsResponse> {
  try {
    const params = new URLSearchParams();
    deliveryZones.forEach((zone) => params.append('deliveryZone', zone));

    const url = buildApiUrl('/v1/sourcing-events', params);
    const response = await authFetch(url);

    if (!response.ok) {
      throw new FetchError('sourcing events', response.statusText);
    }

    const rawData = await response.json() as { data: SourcingEvent[] };
    const filteredData = {
      data: rawData.data.filter((e) => 
        ['open', 'scheduled', 'evaluation'].includes(e.status)
      ),
    };
    
    return sourcingEventsResponseSchema.parse(filteredData);
  } catch {
    // Return mock data when API is unavailable
    await delay(300);
    const filtered = mockSourcingEvents.filter(
      (e) => deliveryZones.includes(e.deliveryZone) && 
             ['open', 'scheduled', 'evaluation'].includes(e.status)
    );
    return {
      data: filtered,
      pagination: {
        nextToken: null,
        totalCount: filtered.length 
      },
    };
  }
}

export async function getSupplierBids(supplierId: string): Promise<BidsResponse> {
  try {
    const params = new URLSearchParams();
    params.set('supplierId', supplierId);

    const url = buildApiUrl('/v1/bids', params);
    const response = await authFetch(url);

    if (!response.ok) {
      throw new FetchError('bids', response.statusText);
    }

    return bidsResponseSchema.parse(await response.json());
  } catch {
    // Return mock data when API is unavailable
    await delay(300);
    const filtered = mockBids.filter((b) => b.supplierId === supplierId);
    return {
      data: filtered,
      pagination: {
        nextToken: null,
        totalCount: filtered.length 
      },
    };
  }
}

export interface SubmitBidRequest {
  eventId: string;
  supplierId: string;
  lines: BidLine[];
  validUntil: string;
  notes?: string;
}

export async function submitBid(request: SubmitBidRequest): Promise<{ bidId: string }> {
  try {
    const url = buildApiUrl('/v1/bids');
    const response = await authFetch(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new SubmitError('bid', response.statusText);
    }

    return response.json() as Promise<{ bidId: string }>;
  } catch {
    // Mock successful submission
    await delay(500);
    return { bidId: `bid-${Date.now()}` };
  }
}

export async function withdrawBid(bidId: string): Promise<void> {
  try {
    const url = buildApiUrl(`/v1/bids/${bidId}/withdraw`);
    const response = await authFetch(url, { method: 'POST' });

    if (!response.ok) {
      throw new WithdrawError('bid', response.statusText);
    }
  } catch {
    // Mock successful withdrawal
    await delay(300);
  }
}
